import { TextField, Box } from "@mui/material";
import PropTypes from "prop-types";
import { useOtp } from "context/OtpContext";
import { OTP_LOGIN_ACTION } from "../../../constants/authConstants";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { performTokenAction } from "../_request";
import MDBox from "components/MDBox";
import SignInWithGoogle from "./SignInWithGoogle";
import TurnstileWidget from "components/TurnstileWidget";
import config from "config/config";

const envConfig = config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"];

const IdentifierInput = ({ setAuthSection }) => {
  const { identifier, isLoading, handleSendOtp, dispatch } = useOtp();
  const navigate = useNavigate();
  const [captchaToken, setCaptchaToken] = useState("");
  const turnstileRef = useRef(null);

  const resetCaptcha = () => {
    setCaptchaToken("");
    turnstileRef.current?.reset();
  };

  const onSubmitForm = async (event) => {
    event.preventDefault();
    await handleSendOtp(captchaToken, resetCaptcha);
  };

  const handleChange = (e) => {
    e.preventDefault();

    dispatch({ type: OTP_LOGIN_ACTION.SET_IDENTIFIER, payload: e.target.value });
  };

  const handleTokenAction = async () => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("token");
    const actionType = queryParams.get("actionType");

    if (!token || !actionType) return;

    dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });

    const result = await performTokenAction({ token, actionType });
    const { success, message } = result.data;

    dispatch({
      type: OTP_LOGIN_ACTION.SET_MESSAGE,
      payload: { type: success ? "success" : "error", message },
    });

    queryParams.delete("token");
    queryParams.delete("actionType");
    navigate(
      {
        pathname: window.location.pathname,
        search: queryParams.toString(),
      },
      { replace: true }
    );
  };

  useEffect(() => {
    handleTokenAction();
  }, []);

  return (
    <form onSubmit={onSubmitForm}>
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        id="identifier"
        label="Email Address or Phone Number"
        name="identifier"
        autoComplete="identifier"
        helperText="Personal email is preferred"
        FormHelperTextProps={{
          sx: {
            color: "#475467",
            marginLeft: 0,
          },
        }}
        value={identifier}
        onChange={handleChange}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#a9a9a9",
            },
            "&:hover fieldset": {
              borderColor: "#a9a9a9",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#808080",
            },
          },
          "& .MuiFormHelperText-root": {
            marginLeft: 0, // Ensures alignment
          },
        }}
      />

      <div style={{ display: "flex", justifyContent: "center", marginTop: "12px" }}>
        <TurnstileWidget
          ref={turnstileRef}
          siteKey={envConfig.TURNSTILE_SITE_KEY}
          onToken={setCaptchaToken}
          onExpire={() => setCaptchaToken("")}
          onError={() => setCaptchaToken("")}
        />
      </div>

      <MDButton
        type="submit"
        variant="contained"
        color="success"
        fullWidth
        disabled={isLoading || !identifier || !captchaToken}
        sx={{
          width: "100%",
          marginTop: "1rem",
          textTransform: "none",
          borderRadius: "8px",
          fontWeight: "500",
          fontSize: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        Continue
      </MDButton>

      <MDBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
          mt: 2.5,
          mb: 2,
        }}
      >
        <MDBox
          sx={{
            height: "1px",
            flex: 1,
            background: "#AFAFAF",
          }}
        />
        <MDTypography
          variant="button"
          sx={{
            color: "#AFAFAF",
            fontSize: "0.875rem",
            fontWeight: 500,
          }}
        >
          or
        </MDTypography>
        <MDBox
          sx={{
            height: "1px",
            flex: 1,
            background: "#AFAFAF",
          }}
        />
      </MDBox>
      <MDButton
        variant="outlined"
        color="secondary"
        fullWidth
        onClick={() => setAuthSection("password")}
        sx={{
          borderRadius: "8px",
          py: 1.5,
          background: "#E8E8E8",
          borderColor: "#E8E8E8",
          marginTop: "1rem",
          color: "#475467",
          textTransform: "none",
          fontWeight: "500",
          fontSize: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          "&:hover": {
            background: "#D1D1D1",
            borderColor: "#D1D5DB",
            color: "#101828",
          },
        }}
      >
        Login with password
      </MDButton>

      <SignInWithGoogle />
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        style={{ marginTop: "24px" }}
      >
        <span style={{ fontSize: 12, maxWidth: "360px", color: "#6B6B6B", LineHeight: "20px" }} x>
          By proceeding, you consent to get calls, SMS messages & emails from Catering Rewards and
          its affiliates to the number provided. Reply “STOP” to opt out. Message & Data rates may
          apply.
        </span>
      </Box>
    </form>
  );
};

export default IdentifierInput;

IdentifierInput.propTypes = {
  setAuthSection: PropTypes.func.isRequired,
};
